/**
 * PURPOSE: Acciones sutiles del detalle de una prueba de Atletismo: ícono chico (`IconAction`) y confirmación en un popover
 *   chico anclado al ícono (`ConfirmPopover`).
 * RESPONSIBILITIES: `IconAction` = botón de solo ícono en slate-400 que se resalta al pasar el mouse, con `aria-label` y
 *   tooltip (`title`). Bloqueado: `aria-disabled` (no `disabled`, para que el tooltip con el motivo se vea siempre) y el clic
 *   no hace nada. `ConfirmPopover` abre al pulsar su ícono, se cierra con Esc, clic afuera o Cancelar, y se dibuja en un
 *   portal para que no lo recorte el scroll horizontal de la tabla. Modal solo para Agregar y Mover (ya existen).
 * COLLABORATORS: grids/gridCells.jsx (acciones de fila), PruebaDetailPanel.jsx (series), Floating UI (posición y portal).
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/IconAction.jsx
 */
import React, { forwardRef, useRef, useState } from "react";
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";

const TONES = {
  indigo: "hover:bg-indigo-50 hover:text-indigo-600 focus-visible:text-indigo-600",
  rose: "hover:bg-rose-50 hover:text-rose-600 focus-visible:text-rose-600",
  emerald: "hover:bg-emerald-50 hover:text-emerald-700 focus-visible:text-emerald-700",
};

export const IconAction = forwardRef(
  ({ icon: Icon, label, disabledReason = null, onClick, tone = "indigo", size = "md", className = "", ...rest }, ref) => {
    const blocked = Boolean(disabledReason);
    return (
      <button
        ref={ref}
        type="button"
        {...rest}
        aria-label={label}
        title={blocked ? disabledReason : label}
        aria-disabled={blocked || undefined}
        onClick={(event) => {
          if (blocked) {
            event.preventDefault();
            return;
          }
          if (onClick) onClick(event);
        }}
        className={`inline-flex shrink-0 items-center justify-center rounded-md text-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-indigo-200 ${
          size === "sm" ? "h-6 w-6 text-[13px]" : "h-7 w-7 text-sm"
        } ${blocked ? "cursor-not-allowed opacity-40" : TONES[tone] || TONES.indigo} ${className}`}
      >
        <Icon aria-hidden="true" />
      </button>
    );
  }
);
IconAction.displayName = "IconAction";

/**
 * `trigger(props)` dibuja el ícono con las props de anclaje (ref, onClick, aria-*). `disabled` evita abrirlo (el ícono ya
 * muestra el motivo). `onConfirm` corre al pulsar el botón principal.
 */
export const ConfirmPopover = ({ trigger, title, message, confirmLabel = "Confirmar", tone = "danger", disabled = false, onConfirm }) => {
  const [open, setOpen] = useState(false);
  const confirmRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-end",
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "alertdialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  return (
    <>
      {trigger({
        ref: refs.setReference,
        ...getReferenceProps({
          onClick: () => {
            if (!disabled) setOpen((current) => !current);
          },
        }),
        "aria-expanded": open,
      })}
      {open ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={confirmRef}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              aria-label={title}
              className="z-[1400] w-72 rounded-xl border border-slate-200 bg-white p-3 text-left font-quickSand shadow-lg"
            >
              <p className="text-xs font-black text-slate-800">{title}</p>
              {message ? <p className="mt-1 text-xs leading-5 text-slate-600">{message}</p> : null}
              <div className="mt-2 flex justify-end gap-1.5">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-2.5 py-1 text-xs font-bold text-slate-500 transition hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  ref={confirmRef}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onConfirm();
                  }}
                  className={`rounded-lg px-2.5 py-1 text-xs font-bold text-white shadow-sm transition ${
                    tone === "danger" ? "bg-rose-600 hover:bg-rose-700" : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  );
};
